import React, { useState } from "react";
import { PageHero } from "../components/PageHero";
import { CTAStrip } from "../components/CTAStrip";
import { Flag, Lightbulb, Palette, BookOpen, TreePine, Sparkles, Heart, Shield, Sun, Music, UtensilsCrossed, Moon } from "lucide-react";

const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/a6c16944-cdbb-43fa-8ac4-8a5b05ddef75/images/a78c61bc5fcba90fb869a6aea00c8ee7d2bd188a04c951099439b17290ba9af6.png";

const stats = [
  { value: "2010", label: "Founded" },
  { value: "1200+", label: "Kids Enrolled" },
  { value: "30+", label: "Teachers" },
];

const facilities = [
  { title: "Bright Classrooms", desc: "Airy, beautifully lit spaces designed to spark curiosity and keep young minds engaged all day long.", icon: Sparkles, tint: "bg-[#DCE7FF]" },
  { title: "Outdoor Play", desc: "Expansive, fully supervised play areas where children build friendships and boundless energy.", icon: TreePine, tint: "bg-[#E6F8E4]" },
  { title: "Modern Library", desc: "A cozy reading haven stocked with hundreds of age-appropriate books, comics, and learning resources.", icon: BookOpen, tint: "bg-[#FFF0DB]" },
  { title: "Arts & Crafts", desc: "A vibrant creative studio where young artists experiment, imagine, and bring their ideas to life.", icon: Palette, tint: "bg-[#FFE1E1]" },
];

const values = [
  { icon: Heart, label: "Care", desc: "Every child's wellbeing comes first — emotionally, physically, and socially.", tint: "bg-[#FFE1E1]", color: "text-kid-orange" },
  { icon: Palette, label: "Creativity", desc: "We celebrate imagination and give every child space to express themselves freely.", tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
  { icon: Lightbulb, label: "Curiosity", desc: "We encourage questions, exploration, and a genuine love for learning every day.", tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
  { icon: Shield, label: "Confidence", desc: "We build belief in every child — in their abilities, their voice, and their worth.", tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
];

const programSchedules = [
  {
    name: "Playgroup", tag: "1–2 yrs", timings: "Mon–Fri · 9:00 am – 12:00 pm",
    activeColor: "bg-kid-softblue text-white", inactiveColor: "bg-[#DCE7FF] text-kid-navy",
    schedule: [
      { time: "9:00 am", activity: "Welcome & Settling In", desc: "Gentle transition from home with soft music, greetings, and free exploration.", icon: Sun, tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
      { time: "9:30 am", activity: "Sensory Play", desc: "Sand, water, textures, and colours — stimulating little senses in a safe, guided setting.", icon: Sparkles, tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
      { time: "10:15 am", activity: "Music & Movement", desc: "Songs, clapping, and gentle movement activities that build rhythm and motor skills.", icon: Music, tint: "bg-[#FFE1E1]", color: "text-kid-orange" },
      { time: "11:00 am", activity: "Outdoor Play", desc: "Supervised play in the garden — walking, exploring, and discovering the natural world.", icon: TreePine, tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
      { time: "11:30 am", activity: "Story & Wind Down", desc: "A calming story session followed by quiet time to prepare little ones for pickup.", icon: BookOpen, tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
    ],
  },
  {
    name: "Nursery", tag: "2–3 yrs", timings: "Mon–Fri · 9:00 am – 1:00 pm",
    activeColor: "bg-kid-orange text-white", inactiveColor: "bg-[#FFE1E1] text-kid-navy",
    schedule: [
      { time: "9:00 am", activity: "Circle Time", desc: "Morning greetings, weather chat, and sharing — building vocabulary and early confidence.", icon: Sun, tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
      { time: "9:30 am", activity: "Art & Craft", desc: "Painting, colouring, and clay work that develops fine motor skills and self-expression.", icon: Palette, tint: "bg-[#FFE1E1]", color: "text-kid-orange" },
      { time: "10:15 am", activity: "Outdoor Play", desc: "Active play in the playground to build energy, balance, and social skills.", icon: TreePine, tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
      { time: "11:00 am", activity: "Language & Stories", desc: "Interactive storytelling, rhymes, and early vocabulary activities to spark communication.", icon: BookOpen, tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
      { time: "11:45 am", activity: "Lunch", desc: "A nutritious, home-style meal in a warm, supervised setting.", icon: UtensilsCrossed, tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
      { time: "12:30 pm", activity: "Free Play & Wind Down", desc: "Relaxed play and quiet activities as children get ready for pickup.", icon: Sparkles, tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
    ],
  },
  {
    name: "Kindergarten", tag: "3–5 yrs", timings: "Mon–Fri · 9:00 am – 3:00 pm",
    activeColor: "bg-kid-amber text-white", inactiveColor: "bg-[#FFF0DB] text-kid-navy",
    schedule: [
      { time: "9:00 am", activity: "Morning Assembly", desc: "Songs, pledges, and morning motivation — setting a focused, energetic tone for the day.", icon: Flag, tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
      { time: "9:30 am", activity: "Guided Learning", desc: "Phonics, early reading, and number concepts through structured and interactive activities.", icon: BookOpen, tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
      { time: "10:30 am", activity: "Outdoor Play", desc: "Physical education, group games, and free play to build teamwork and fitness.", icon: TreePine, tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
      { time: "11:15 am", activity: "Art & Creativity", desc: "Painting, craft projects, and creative expression to celebrate every child's imagination.", icon: Palette, tint: "bg-[#FFE1E1]", color: "text-kid-orange" },
      { time: "12:00 pm", activity: "Lunch & Rest", desc: "A wholesome meal followed by quiet time to recharge for the afternoon.", icon: UtensilsCrossed, tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
      { time: "1:00 pm", activity: "STEM & Projects", desc: "Hands-on science, problem-solving, and group project work to spark curiosity.", icon: Lightbulb, tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
      { time: "2:00 pm", activity: "Music & Story Time", desc: "Songs, rhymes, and engaging stories that close the learning day on a joyful note.", icon: Music, tint: "bg-[#FFE1E1]", color: "text-kid-orange" },
    ],
  },
  {
    name: "Daycare", tag: "1–5 yrs", timings: "Mon–Fri · 8:00 am – 6:00 pm",
    activeColor: "bg-emerald-500 text-white", inactiveColor: "bg-[#E6F8E4] text-kid-navy",
    schedule: [
      { time: "8:00 am", activity: "Early Drop-off", desc: "Warm welcome for early arrivals with calm, free-play activities to ease into the day.", icon: Sun, tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
      { time: "9:00 am", activity: "Morning Program", desc: "Age-appropriate learning and play activities aligned with each child's enrolled program.", icon: BookOpen, tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
      { time: "12:00 pm", activity: "Lunch", desc: "Nutritious, home-style meals prepared fresh daily in our in-house kitchen.", icon: UtensilsCrossed, tint: "bg-[#FFF0DB]", color: "text-kid-amber" },
      { time: "1:00 pm", activity: "Nap & Rest Time", desc: "Supervised nap time with cozy mats and soft music for a peaceful afternoon rest.", icon: Moon, tint: "bg-[#DCE7FF]", color: "text-kid-softblue" },
      { time: "2:30 pm", activity: "Afternoon Activity", desc: "Creative and engaging activities to refresh and re-energise after rest.", icon: Palette, tint: "bg-[#FFE1E1]", color: "text-kid-orange" },
      { time: "4:00 pm", activity: "Evening Snack", desc: "A light, healthy snack to keep energy levels up through the evening.", icon: UtensilsCrossed, tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
      { time: "4:30 pm", activity: "Free Play & Stories", desc: "Relaxed play, storytelling, and calm activities until parents arrive for pickup.", icon: Sparkles, tint: "bg-[#E6F8E4]", color: "text-emerald-500" },
    ],
  },
];

export default function About() {
  const [activeProgram, setActiveProgram] = useState(0);
  return (
    <div data-testid="page-about">
      <PageHero title="About Our School" breadcrumb="About" illustration={ABOUT_IMG} illustrationAlt="Teacher with kids" />

      {/* Our Story */}
      <section className="bg-white pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="section-label">Our Story</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-kid-navy">Built on Love, Learning, and Laughter</h2>
            <p className="mt-5 text-kid-navy/75 leading-relaxed">
              Founded in 2010 with a simple but powerful belief — that every child deserves a place where they feel
              safe, celebrated, and inspired — our school has grown into one of the community's most beloved learning
              environments. We don't just teach lessons; we build confidence, kindle curiosity, and nurture the unique
              spark that lives in every child who walks through our doors.
            </p>
            <p className="mt-4 text-kid-navy/75 leading-relaxed">
              From our caring teachers to our thoughtfully designed spaces, everything here is crafted with one goal
              in mind: helping your child thrive — academically, socially, and joyfully.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="text-center bg-kid-bg rounded-2xl py-5 px-3" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="text-3xl md:text-4xl font-bold text-kid-navy">{s.value}</div>
                  <div className="mt-1 text-sm text-kid-navy/60 font-medium">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <img src={ABOUT_IMG} alt="Classroom" className="rounded-3xl shadow-soft w-full object-cover" />
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-kid-bg py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center">
            <span className="section-label">Our Mission &amp; Vision</span>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="kid-card relative overflow-hidden" data-testid="mission-card">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-kid-softblue/10 rounded-full" aria-hidden="true" />
              <Flag size={36} strokeWidth={2.2} className="text-kid-amber relative" />
              <h3 className="mt-4 text-2xl font-bold text-kid-navy relative">Our Mission</h3>
              <p className="mt-3 text-kid-navy/75 relative">
                To provide a safe, inclusive, and joyfully engaging environment where every young learner feels
                seen and valued. We empower children to explore their interests, ask bold questions, and develop
                the skills — and the heart — they need to grow into their very best selves.
              </p>
            </div>
            <div className="kid-card relative overflow-hidden" data-testid="vision-card">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-kid-pink/20 rounded-full" aria-hidden="true" />
              <Lightbulb size={36} strokeWidth={2.2} className="text-kid-amber relative" />
              <h3 className="mt-4 text-2xl font-bold text-kid-navy relative">Our Vision</h3>
              <p className="mt-3 text-kid-navy/75 relative">
                We envision a generation of confident, creative, and compassionate individuals — children who are
                not just ready for the next grade, but ready for life. We dream of a world shaped by the kindness
                and curiosity we cultivate in our classrooms every single day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center">
            <span className="section-label">Our Facilities</span>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {facilities.map((f) => (
              <div key={f.title} className="kid-card text-center" data-testid={`facility-${f.title.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className={`w-20 h-20 mx-auto rounded-2xl ${f.tint} flex items-center justify-center`}>
                  <f.icon size={32} strokeWidth={2.2} className="text-kid-navy" />
                </div>
                <h3 className="mt-4 font-bold text-kid-navy">{f.title}</h3>
                <p className="mt-2 text-sm text-kid-navy/70">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-kid-bg py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="section-label">What We Stand For</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-kid-navy">Our Core Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.label} className="kid-card text-center" data-testid={`value-${v.label.toLowerCase()}`}>
                <div className={`w-16 h-16 mx-auto rounded-full ${v.tint} flex items-center justify-center`}>
                  <v.icon size={28} strokeWidth={2.2} className={v.color} />
                </div>
                <h3 className="mt-4 text-lg font-bold text-kid-navy">{v.label}</h3>
                <p className="mt-2 text-sm text-kid-navy/70 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A Day at Spring Kids */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="section-label">A Typical Day</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold text-kid-navy">A Day at Spring Kids</h2>
            <p className="mt-3 text-kid-navy/70 max-w-xl mx-auto">
              Every program has a thoughtfully structured day that balances learning, play, rest, and creativity.
            </p>
          </div>

          {/* Program Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {programSchedules.map((p, i) => (
              <button
                key={p.name}
                onClick={() => setActiveProgram(i)}
                className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${activeProgram === i ? p.activeColor : p.inactiveColor + " hover:opacity-80"}`}
                data-testid={`day-tab-${p.name.toLowerCase()}`}
              >
                {p.name} <span className="text-xs font-normal opacity-70">{p.tag}</span>
              </button>
            ))}
          </div>

          {/* Schedule */}
          <div className="max-w-3xl mx-auto">
            <p className="text-center text-sm font-semibold text-kid-navy/50 mb-6">
              {programSchedules[activeProgram].timings}
            </p>
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-kid-bg hidden sm:block" />
              <div className="space-y-5">
                {programSchedules[activeProgram].schedule.map((item, i) => (
                  <div key={item.time} className="flex gap-5 items-start" data-testid={`day-schedule-${i}`}>
                    <div className={`w-12 h-12 rounded-full ${item.tint} flex items-center justify-center shrink-0 relative z-10`}>
                      <item.icon size={22} strokeWidth={2.2} className={item.color} />
                    </div>
                    <div className="kid-card flex-1 py-4 px-5">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-kid-amber bg-[#FFF0DB] px-2 py-0.5 rounded-full">{item.time}</span>
                        <h3 className="font-bold text-kid-navy">{item.activity}</h3>
                      </div>
                      <p className="text-sm text-kid-navy/70 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
